import { downloadFileFromData } from '@accredible-frontend-v2/utils/download-file-helper';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import {
  CustomFrameworkColumnMapping,
  CustomFrameworkIssueSeverity,
  CustomFrameworkIssuesData,
  CustomFrameworkPreview,
  CustomFrameworkValidateResponse,
  ImpactedGroupsData,
  PageParams,
  SkillFrameworkVersionDiffData,
} from '../../entities/custom-skills-framework.model';
import { DashboardApiService } from '../db-api.service';

const FRAMEWORKS_ENDPOINT = '/v1/corporation/skill_frameworks';
const UPLOADS_ENDPOINT = '/v1/corporation/skill_framework_uploads';
const TEMPLATE_FILE_NAME = 'skill-framework-template';

@Injectable({
  providedIn: 'root',
})
export class CorporationSkillsFrameworksUploadApiService extends DashboardApiService {
  protected override _getHeaders(method: string, endpoint: string): { [header: string]: string } {
    const headers = super._getHeaders(method, endpoint);
    return {
      ...headers,
      'accredible-department-id': this.embedding.departmentId.toString(),
    };
  }

  previewCustomFramework(s3Key: string): Observable<CustomFrameworkPreview> {
    return this._post(`${FRAMEWORKS_ENDPOINT}/preview`, { s3_key: s3Key }).pipe(
      map((res) => this._handleResponse(res)),
      catchError((res) => this._handleError(res)),
    );
  }

  validateCustomFramework(
    s3Key: string,
    mapping: CustomFrameworkColumnMapping,
  ): Observable<CustomFrameworkValidateResponse> {
    return this._post(`${FRAMEWORKS_ENDPOINT}/validate`, { s3_key: s3Key, mapping }).pipe(
      map((res) => this._handleResponse(res)),
      catchError((res) => this._handleError(res)),
    );
  }

  validateCustomFrameworkVersion(
    skillFrameworkId: number,
    s3Key: string,
    mapping: CustomFrameworkColumnMapping,
  ): Observable<CustomFrameworkValidateResponse> {
    return this._post(`${FRAMEWORKS_ENDPOINT}/${skillFrameworkId}/validate`, {
      s3_key: s3Key,
      mapping,
    }).pipe(
      map((res) => this._handleResponse(res)),
      catchError((res) => this._handleError(res)),
    );
  }

  getUploadIssues(
    uploadId: number,
    pageData: PageParams = { page: 1, pageSize: 20 },
    severity?: CustomFrameworkIssueSeverity,
  ): Observable<CustomFrameworkIssuesData> {
    const query = this._buildQueryString({
      page: pageData.page,
      page_size: pageData.pageSize,
      severity,
    });
    return this._get(`${UPLOADS_ENDPOINT}/${uploadId}/issues?${query}`).pipe(
      map((res) => this._handleResponse(res)),
      catchError((res) => this._handleError(res)),
    );
  }

  downloadTemplate(): Observable<void> {
    const endpoint = `${FRAMEWORKS_ENDPOINT}/template`;
    return this.http
      .get(this.apiUrl + endpoint, {
        headers: this._getHeaders('GET', endpoint),
        responseType: 'text',
      })
      .pipe(
        tap((data) => downloadFileFromData(data, TEMPLATE_FILE_NAME)),
        map(() => undefined),
        catchError((res) => this._handleError(res)),
      );
  }

  getVersionDiff(
    skillFrameworkId: number,
    frameworkUploadId: number,
    pageData: PageParams = { page: 1, pageSize: 50 },
    withImpact = false,
  ): Observable<SkillFrameworkVersionDiffData> {
    const query = this._buildQueryString({
      framework_upload_id: frameworkUploadId,
      page: pageData.page,
      page_size: pageData.pageSize,
      with_impact: withImpact || null,
    });
    return this._get(`${FRAMEWORKS_ENDPOINT}/${skillFrameworkId}/version_diff?${query}`).pipe(
      map((res) => this._handleResponse(res)),
      catchError((res) => this._handleError(res)),
    );
  }

  getImpactedGroups(
    skillFrameworkId: number,
    skillId: number,
    pageData: PageParams = { page: 1, pageSize: 10 },
  ): Observable<ImpactedGroupsData> {
    const query = this._buildQueryString({
      from_skill_id: skillId,
      page: pageData.page,
      page_size: pageData.pageSize,
    });
    return this._get(`${FRAMEWORKS_ENDPOINT}/${skillFrameworkId}/impacted_groups?${query}`).pipe(
      map((res) => this._handleResponse(res)),
      catchError((res) => this._handleError(res)),
    );
  }
}
